import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Building2, Car, Info } from "lucide-react";

// Distancias reales aproximadas desde el centro de Madrid (km) proyectadas a la escala del SVG.
const SCALE = 240 / 70; // 70 km = radio exterior de 240 px
const CENTER = 300;

const cities = [
  { name: "Alcalá de Henares", dx: 28.9, dy: 7.2, anchor: "start", ldy: -14 },
  { name: "Guadalajara", dx: 46.3, dy: 23.6, anchor: "start", ldy: -14 },
  { name: "Getafe", dx: -2.4, dy: -12.0, anchor: "middle", ldy: 22 },
  { name: "Aranjuez", dx: 8.6, dy: -42.8, anchor: "start", ldy: 5 },
  { name: "Toledo", dx: -27.5, dy: -61.4, anchor: "middle", ldy: 22 },
  { name: "El Escorial", dx: -37.7, dy: 19.2, anchor: "end", ldy: -14 },
  { name: "Segovia", dx: -34.5, dy: 58.4, anchor: "middle", ldy: -16 },
];

const rings = [
  { km: 30, opacity: 0.5 },
  { km: 50, opacity: 0.35 },
  { km: 70, opacity: 0.25 },
];

const buildSvg = () => {
  const ringsMarkup = rings
    .map(
      (r) => `
    <circle cx="${CENTER}" cy="${CENTER}" r="${r.km * SCALE}" fill="none" stroke="#7C3AED"
      stroke-opacity="${r.opacity}" stroke-width="1.5" stroke-dasharray="6 6" />
    <text x="${CENTER + 6}" y="${CENTER - r.km * SCALE + 18}" font-size="16" fill="#7C3AED"
      fill-opacity="0.85" font-weight="600">${r.km} km</text>`
    )
    .join("");

  const citiesMarkup = cities
    .map((c) => {
      const x = CENTER + c.dx * SCALE;
      const y = CENTER - c.dy * SCALE;
      const tx = c.anchor === "end" ? x - 10 : c.anchor === "start" ? x + 10 : x;
      return `
    <circle cx="${x}" cy="${y}" r="6" fill="#0F172A" fill-opacity="0.6" />
    <text x="${tx}" y="${y + c.ldy}" text-anchor="${c.anchor}" font-size="17" fill="#475569">${c.name}</text>`;
    })
    .join("");

  return `
  <svg viewBox="0 0 600 600" width="100%" height="auto" role="img"
    aria-label="Mapa de cobertura: Comunidad de Madrid y 70 km alrededor"
    style="font-family: Manrope, sans-serif; display:block; width:100%; height:auto;">
    <defs>
      <radialGradient id="coverageFill" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#2563EB" stop-opacity="0.20" />
        <stop offset="70%" stop-color="#7C3AED" stop-opacity="0.09" />
        <stop offset="100%" stop-color="#7C3AED" stop-opacity="0.02" />
      </radialGradient>
    </defs>
    <circle cx="${CENTER}" cy="${CENTER}" r="${70 * SCALE}" fill="url(#coverageFill)" />
    ${ringsMarkup}
    ${citiesMarkup}
    <circle cx="${CENTER}" cy="${CENTER}" r="26" fill="#2563EB" fill-opacity="0.15" />
    <circle cx="${CENTER}" cy="${CENTER}" r="11" fill="#2563EB" />
    <circle cx="${CENTER}" cy="${CENTER}" r="4" fill="#ffffff" />
    <text x="${CENTER - 20}" y="${CENTER - 34}" text-anchor="end" font-size="19" font-weight="700"
      fill="#0F172A">Madrid</text>
  </svg>`;
};

export default function CoverageMap({ variant = "home" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const isAgency = variant === "agencias";

  return (
    <section
      id="cobertura"
      className="section-spacing bg-galvani-bg"
      data-testid="coverage-map-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-cyan font-body font-semibold mb-4">
              Zona de cobertura
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-5">
              Comunidad de Madrid y{" "}
              <span className="gradient-text">70 km alrededor</span>
            </h2>
            <p className="font-body text-base text-galvani-textLight leading-relaxed mb-8">
              {isAgency
                ? "Grabamos en nuestro estudio de Madrid o montamos el estudio en las instalaciones de tu cliente, en toda la Comunidad de Madrid y hasta unos 70 km alrededor. Así puedes prometer fechas sin sorpresas de logística."
                : "Puedes venir a nuestro estudio en el centro de Madrid o hacer que el estudio vaya a ti: grabamos en tus instalaciones en toda la Comunidad de Madrid y hasta unos 70 km alrededor."}
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-base text-galvani-text mb-1">
                    Nuestro estudio
                  </h3>
                  <p className="font-body text-sm text-galvani-textLight">
                    C/ Fernando el Católico 57, local — 28015 Madrid
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-galvani-cyan to-galvani-blue flex items-center justify-center flex-shrink-0">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-base text-galvani-text mb-1">
                    Desplazamiento incluido
                  </h3>
                  <p className="font-body text-sm text-galvani-textLight">
                    Dentro del radio de 70 km montamos y desmontamos el estudio sin coste extra de
                    viaje.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="font-body text-sm text-blue-900">
                  ¿Estás algo más lejos? Escríbenos y lo valoramos caso por caso.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="relative rounded-3xl bg-white border border-slate-200 shadow-xl p-4 sm:p-6 overflow-hidden">
              <div
                className="w-full"
                data-testid="coverage-map-svg"
                dangerouslySetInnerHTML={{ __html: buildSvg() }}
              />

              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 justify-center">
                <span className="inline-flex items-center gap-2 font-body text-xs text-galvani-textLight">
                  <span className="w-2.5 h-2.5 rounded-full bg-galvani-blue" />
                  Nuestro estudio
                </span>
                <span className="inline-flex items-center gap-2 font-body text-xs text-galvani-textLight">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-500" />
                  Ciudades de referencia
                </span>
                <span className="inline-flex items-center gap-2 font-body text-xs text-galvani-textLight">
                  <MapPin className="w-3.5 h-3.5 text-galvani-purple" />
                  Distancias aproximadas
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
